import React, { useEffect, useMemo, useState } from 'react';
import { MagnifyingGlass, Funnel } from '@phosphor-icons/react';
import { api } from '../api';
import {
  formatLongDate, todayISO, formatTime, timeToMinutes, countdownTo,
  deliveryStatus, canAddVehicle,
} from '../helpers';
import DeliveryCard from '../components/DeliveryCard';
import { showToast } from '../components/Toast';

/**
 * Delivery Board — today's handovers, grouped by delivery time.
 * Per the delivery-board design handoff.
 */

const CHIPS = [
  { id: 'READY',       label: 'Ready',       cls: 'ready' },
  { id: 'IN_PROGRESS', label: 'In progress', cls: 'prog' },
  { id: 'BLOCKED',     label: 'Blocked',     cls: 'blocked' },
  { id: 'ALL',         label: 'Total today', cls: 'total' },
];

export default function Delivery({ session, refreshKey, onUpdate, onAdd, onRefresh }) {
  const role = session?.role;
  const [today, setToday]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter]   = useState('ALL');
  const [search, setSearch]   = useState('');
  // Countdowns are relative to now, so re-render every minute to keep them honest
  const [, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.todayVehicles()
      .then((v) => { if (!cancelled) setToday(v); })
      .catch((e) => showToast('Failed: ' + e.message))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [refreshKey]);

  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 60000);
    return () => clearInterval(t);
  }, []);

  // Only sold cars get handed to a customer — demo, stock, used and customer
  // cars belong on the dashboard, not on a handover board. Rows predating the
  // deal_status column default to SOLD, same as everywhere else.
  const board = useMemo(
    () => today.filter((v) => !v.handed_over_at && (v.deal_status || 'SOLD') === 'SOLD'),
    [today]
  );

  const counts = useMemo(() => {
    const c = { READY: 0, IN_PROGRESS: 0, BLOCKED: 0, NOT_STARTED: 0, ALL: board.length };
    board.forEach((v) => { c[deliveryStatus(v)] += 1; });
    return c;
  }, [board]);

  const visible = useMemo(() => {
    let list = filter === 'ALL' ? board : board.filter((v) => deliveryStatus(v) === filter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter((v) =>
        (v.stock_number || '').toLowerCase().includes(q) ||
        (v.model || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [board, filter, search]);

  // Group by delivery time, ascending; blocked first inside a slot.
  const groups = useMemo(() => {
    const map = new Map();
    visible.forEach((v) => {
      const key = timeToMinutes(v.delivery_time);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(v);
    });
    return [...map.entries()]
      .sort((a, b) => a[0] - b[0])
      .map(([minutes, cars]) => ({
        minutes,
        label: minutes > 24 * 60 ? 'No time set' : formatTime(cars[0].delivery_time),
        cars: cars.sort((a, b) => {
          const ab = a.blocker_note ? 0 : 1, bb = b.blocker_note ? 0 : 1;
          return ab - bb || String(a.model).localeCompare(String(b.model));
        }),
      }));
  }, [visible]);

  // Countdown badge shows on the next slot that has not passed yet.
  const nextSlot = groups.find((g) => {
    const cd = countdownTo(g.minutes);
    return cd && cd.text !== 'overdue';
  });

  async function save(id, patch, msg) {
    // Optimistic: the board is glanced at constantly, a round-trip flicker shows
    const prev = today;
    setToday((list) => list.map((x) => (x.id === id ? { ...x, ...patch } : x)));
    try {
      await api.updateVehicle(id, patch);
      showToast(msg);
      onRefresh?.();
    } catch (e) {
      setToday(prev);
      showToast('Failed: ' + e.message);
    }
  }

  async function flag(v) {
    if (v.blocker_note) {
      if (!window.confirm(`Clear the blocker on ${v.model}?\n\n"${v.blocker_note}"`)) return;
      return save(v.id, { blocker_note: null }, `${v.model} unblocked`);
    }
    const reason = window.prompt(`What is blocking ${v.model} (${v.stock_number})?`);
    if (!reason || !reason.trim()) return;
    return save(v.id, { blocker_note: reason.trim() }, `${v.model} flagged as blocked`);
  }

  async function handOver(v) {
    if (!window.confirm(`Hand over ${v.model} (${v.stock_number}) to the customer now?`)) return;
    const prev = today;
    setToday((list) => list.map((x) => (x.id === v.id ? { ...x, handed_over_at: new Date().toISOString() } : x)));
    try {
      await api.handOver(v.id);
      showToast(`${v.model} handed over`);
      onRefresh?.();
    } catch (e) {
      setToday(prev);
      showToast('Failed: ' + e.message);
    }
  }

  if (loading) return <div className="loading">Loading deliveries…</div>;

  return (
    <div>
      <div className="ph">
        <div>
          <div className="pt">Delivery Board</div>
          <div className="ps">
            {board.length} car{board.length === 1 ? '' : 's'} due {formatLongDate(todayISO())}
            {nextSlot ? ` · next handover at ${nextSlot.label}` : ''}
          </div>
        </div>
        <div className="ph-actions" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <div className="dboard-search">
            <MagnifyingGlass size={15} weight="bold" color="var(--muted)" />
            <input placeholder="Search stock or model" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          {canAddVehicle(role) && <button className="ab p" onClick={onAdd}>+ Add Vehicle</button>}
        </div>
      </div>

      <div className="dchips">
        {CHIPS.map((c) => (
          <button
            key={c.id}
            className={`dchip ${c.cls} ${filter === c.id ? 'on' : ''}`}
            onClick={() => setFilter((f) => (f === c.id && c.id !== 'ALL' ? 'ALL' : c.id))}
          >
            <span className="dchip-dot" />
            <span className="dchip-n num">{counts[c.id]}</span>
            <span className="dchip-l">{c.label}</span>
          </button>
        ))}
        <span className="dchip-sort"><Funnel size={13} weight="duotone" />Sorted by delivery time</span>
      </div>

      {groups.length === 0 ? (
        <div className="loading">
          {board.length === 0 ? 'No deliveries scheduled for today.' : 'No cars match this filter.'}
        </div>
      ) : (
        <div className="dgroups">
          {groups.map((g) => {
            const cd = g === nextSlot ? countdownTo(g.minutes) : null;
            return (
              <div key={g.minutes}>
                <div className="dslot">
                  <span className="dslot-t">{g.label}</span>
                  {cd && <span className={`dslot-cd ${cd.urgent ? 'urgent' : ''}`}>{cd.text}</span>}
                  <span className="dslot-n">{g.cars.length} car{g.cars.length === 1 ? '' : 's'}</span>
                  <span className="dslot-rule" />
                </div>
                <div className="dgrid2">
                  {g.cars.map((v) => (
                    <DeliveryCard
                      key={v.id}
                      vehicle={v}
                      onOpen={onUpdate}
                      onFlag={flag}
                      onHandOver={handOver}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
